/**
 * 应用管理模块类型定义
 * 包含应用 CRUD、API Key 管理等接口的请求和响应类型
 */

import type { UnixTimestamp } from '../types/common';

// ==================== 应用信息 ====================

/**
 * 应用状态枚举
 *
 * 对齐 OpenAPI: AppInfo.status / AppStatusRequest.status
 *
 * - `DISABLED=0`：已暂停，API Key 失效
 * - `ENABLED=1`：已启用，API Key 正常
 * - `DELETED=2`：已软删除，列表不可见（通常由 `DELETE /apps/{id}` 触发；
 *   `PATCH /apps/{id}/status` 不建议设为 2）
 */
export enum AppStatus {
  /** 禁用状态，API Key 立即失效 */
  DISABLED = 0,
  /** 启用状态，API Key 正常使用 */
  ENABLED = 1,
  /** 软删除状态，不在列表中显示 */
  DELETED = 2,
}

/**
 * App 级 RPC 服务轻量状态（列表 / 详情）
 *
 * 对齐 YApi 953: RpcServiceStatus。不含 Token 或 Endpoint URL。
 */
export type RpcServiceStatusCode = 'enabled' | 'disabled' | 'unavailable';

export interface RpcServiceStatus {
  /**
   * - `enabled`：RPC 已启用
   * - `disabled`：未启用或已禁用
   * - `unavailable`：endpoints 暂时不可用
   */
  status: RpcServiceStatusCode;
  /** 仅 `status === 'enabled'` 时为 true，供前端快捷判断 */
  enabled: boolean;
  /** 当前动态网络数量 */
  networkCount: number;
}

export type EnergyRentalServiceStatusCode = 'enabled' | 'disabled' | 'unavailable';

export interface EnergyRentalServiceStatus {
  status: EnergyRentalServiceStatusCode;
  enabled: boolean;
  enabledAt?: UnixTimestamp | null;
}

/**
 * 应用基本信息（不含明文 API Key 与 30 天统计）
 *
 * 对齐 YApi / OpenAPI: AppInfo / AppListItem
 *
 * `GET /apps` 列表项使用此类型；
 * `GET /apps/{id}` 详情使用扩展的 {@link AppDetail}。
 */
export interface AppInfo {
  /** 应用唯一 ID（格式 `app_xxx`） */
  id: string;
  /** 应用名称 */
  name: string;
  /** API Key 前缀（前 8 位），用于在列表中识别密钥身份 */
  keyPrefix: string;
  /** 应用状态，见 {@link AppStatus} */
  status: AppStatus;
  /** 创建时间（Unix 秒级时间戳） */
  createdAt: UnixTimestamp;
  /** 最后更新时间（Unix 秒级时间戳） */
  updatedAt: UnixTimestamp;
  /** 最后使用时间（Unix 秒级时间戳），从未使用时为 null */
  lastUsedAt: UnixTimestamp | null;
  /** 是否启用 WebSocket 推送 */
  enable_ws: boolean;
  /** 是否启用 Webhook 推送 */
  enable_webhook: boolean;
  /** Webhook 推送地址；未配置则为 null */
  webhook_url: string | null;
  /** 轻量 RPC 状态（列表用于 RPC 按钮分流） */
  rpcService: RpcServiceStatus;
  /** Tron 能量租赁轻量状态（/apps 列表聚合） */
  energyRentalService: EnergyRentalServiceStatus;
}

/**
 * 最近 30 天调用统计
 *
 * 对齐 OpenAPI: AppDetail.stats30d
 */
export interface AppStats30d {
  /** 最近 30 天通过此应用的 API 调用总次数 */
  totalCalls30d: number;
  /** 最近 30 天通过此应用消耗的积分总量 */
  creditsCharged30d: number;
  /** 最近 30 天扣除的 USDT 余额（保留 6 位小数的字符串） */
  usdtDeducted30d: string;
}

/**
 * 应用详情（含最近 30 天调用统计）
 *
 * 对齐 OpenAPI: AppDetail schema
 *
 * `GET /apps/{id}` 返回此类型，列表接口仍使用更精简的 {@link AppInfo}。
 */
export interface AppDetail extends AppInfo {
  /** 最近 30 天的 API 调用统计 */
  stats30d: AppStats30d;
}

/**
 * 创建应用请求参数
 */
export interface CreateAppRequest {
  /** 应用名称，长度 1-100 个字符 */
  name: string;
  /** 是否启用 WebSocket 推送；不传则不写入上游推送配置 */
  enable_ws?: boolean;
  /** 是否启用 Webhook 推送；不传则不写入上游推送配置 */
  enable_webhook?: boolean;
  /** Webhook 推送地址；传 null 表示清空 */
  webhook_url?: string | null;
}

/**
 * 创建应用响应数据
 * 包含明文 API Key（仅此时可见，请妥善保存）
 */
export interface CreateAppResult {
  /** 应用唯一 ID */
  id: string;
  /** 应用名称 */
  name: string;
  /** 完整明文 API Key，**创建后仅返回一次**，请立即保存 */
  key: string;
  /** API Key 前缀（前 8 位），用于在列表中识别密钥身份 */
  keyPrefix: string;
  /** 创建时间（Unix 秒级时间戳） */
  createdAt: UnixTimestamp;
}

/**
 * 应用列表响应数据
 */
export interface AppListResult {
  /** 用户拥有的应用列表 */
  apps: AppInfo[];
}

/**
 * 敏感操作挑战响应数据
 * 用于重置 API Key 或删除应用前的签名验证
 */
export interface AppChallengeResult {
  /** 随机一次性字符串 */
  nonce: string;
  /** 需要钱包签名的完整消息文本 */
  message: string;
}

/**
 * 重置 API Key 请求参数
 */
export interface ResetKeyRequest {
  /** 使用钱包私钥对挑战消息签名后的十六进制字符串 */
  signature: string;
}

/**
 * 重置 API Key 响应数据
 * 包含新的明文密钥（重置后仅返回一次）
 */
export interface ResetKeyResult {
  /** 新的完整明文 API Key，**重置后仅返回一次**，请立即保存 */
  key: string;
  /** 新 API Key 的前缀 */
  keyPrefix: string;
  /** 更新时间（Unix 秒级时间戳） */
  updatedAt: UnixTimestamp;
}

/**
 * 删除应用请求参数
 */
export interface DeleteAppRequest {
  /** 使用钱包私钥对挑战消息签名后的十六进制字符串 */
  signature: string;
}

/**
 * 更新应用状态请求参数
 *
 * 对齐 OpenAPI: AppStatusRequest schema。
 *
 * **签名流程**：本接口需要先调用 {@link getAppChallenge} 获取挑战消息，
 * 用户钱包对消息 personal_sign 后携带 signature 调用，与 `deleteApp` / `resetApiKey` 一致。
 */
export interface UpdateAppStatusRequest {
  /**
   * 目标应用状态
   *
   * - `0`：暂停，API Key 立即失效
   * - `1`：启用，API Key 恢复正常
   * - `2`：软删除（建议直接使用 `DELETE /apps/{id}`，此处理论上也能设置）
   */
  status: AppStatus;
  /** 使用钱包私钥对 `/apps/challenge` message 签名后的十六进制字符串 */
  signature: string;
}

/**
 * 更新应用状态响应数据
 */
export interface UpdateAppStatusResult {
  /** 是否更新成功 */
  updated: boolean;
}

/**
 * 应用交易推送配置更新请求体
 *
 * 对齐 OpenAPI: AppPushConfigUpdateRequest。
 *
 * 本接口必须携带签名，并至少传入一个配置字段：
 * - `enable_ws`：是否启用 WebSocket 推送
 * - `enable_webhook`：是否启用 Webhook 推送
 * - `webhook_url`：Webhook 推送地址；传 null 表示清空
 */
export interface AppPushConfigUpdateRequest {
  /** 是否启用 WebSocket 推送 */
  enable_ws?: boolean;
  /** 是否启用 Webhook 推送 */
  enable_webhook?: boolean;
  /** Webhook 推送地址；传 null 表示清空 */
  webhook_url?: string | null;
  /** 对 `/apps/challenge` 返回 message 的以太坊 personal_sign 签名 */
  signature: string;
}

/**
 * 应用交易推送配置更新结果
 */
export interface AppPushConfigUpdateResult {
  /** 是否更新成功 */
  updated: boolean;
}

// ==================== App RPC / WSS（YApi 953） ====================

/** RPC 签名挑战用途 */
export type RpcChallengeAction = 'enable' | 'rotate';

/**
 * RPC 挑战请求体
 *
 * 对齐 YApi: POST /apps/{appId}/rpc/challenge
 */
export interface RpcChallengeRequest {
  /** 省略时服务端按 enable 处理 */
  action?: RpcChallengeAction;
}

/**
 * RPC 挑战响应（与 apps challenge 同形）
 */
export type AppRpcChallengeResult = AppChallengeResult;

/**
 * 启用 RPC 请求体
 *
 * 对齐 YApi: POST /apps/{appId}/rpc/enable
 * 生产环境 signature 必填；Beta 可由服务配置允许省略。
 */
export interface EnableAppRpcRequest {
  /** 对 enable 挑战 message 的 personal_sign 签名 */
  signature?: string;
}

/**
 * 轮换共享 RPC Token 请求体
 *
 * 对齐 YApi: POST /apps/{appId}/rpc/rotate
 */
export interface RotateAppRpcRequest {
  /** 对 rotate 挑战 message 的 personal_sign 签名 */
  signature: string;
}

/**
 * 单网络用量
 *
 * 对齐 YApi: RpcUsage
 */
export interface RpcUsage {
  /** 过去 24 小时请求次数 */
  requests24h: number;
  /** 累计扣除 Credits */
  creditsDeducted: number;
}

/**
 * 单网络 Endpoint
 *
 * 对齐 YApi: RpcNetworkEndpoint。
 * `httpEndpoint` / `wssEndpoint` 含共享 Token，属敏感凭据，勿 persist / 全量打日志。
 */
export interface RpcNetworkEndpoint {
  /** 网络 key，如 ethereum-mainnet */
  network: string;
  /** 展示名，如 Ethereum Mainnet */
  displayName: string;
  /** 是否支持 WSS */
  supportsWss: boolean;
  /** HTTPS Endpoint（含 Token） */
  httpEndpoint: string;
  /** WSS Endpoint；仅 supportsWss=true 时返回 */
  wssEndpoint?: string;
  /** 用量统计 */
  usage: RpcUsage;
}

/**
 * RPC 凭据与网络列表
 *
 * 对齐 YApi: RpcCredentials（enable / endpoints / rotate 共用）
 */
export interface RpcCredentials {
  /** 是否已启用 */
  enabled: boolean;
  /**
   * App 级共享 RPC Token（敏感）。
   * 所有网络与 HTTP/WSS 共用；禁止写入 localStorage / 埋点 / 全量日志。
   */
  token: string;
  /** Token 版本，自 1 起 */
  version: number;
  /** 当前可用网络列表 */
  networks: RpcNetworkEndpoint[];
}

/** enable / endpoints / rotate 成功时的 data */
export type AppRpcCredentialsResult = RpcCredentials;

/**
 * 查询 endpoints 可选参数
 */
export interface GetAppRpcEndpointsParams {
  /** 按 network key 或 displayName 模糊过滤，不区分大小写 */
  search?: string;
}
